import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";

const ACTION_LABELS = {
  created: "Created",
  updated: "Updated",
  deleted: "Deleted",
  restored: "Restored",
};

function formatWhen(value) {
  if (!value) return { date: "—", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: String(value), time: "" };
  return {
    date: date.toLocaleDateString("en-GB"),
    time: date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
  };
}

const FIELD_LABELS = {
  organization: "Organization",
  organization_id: "Organization",
  user_current_active_organization_id: "Organization",
  application_user_id: "User",
  full_name: "Name",
  email: "Email",
  is_active: "Active",
  is_deleted: "Deleted",
};

function readableWords(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function fieldLabel(field) {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const words = readableWords(String(field).replace(/_id$/i, ""));
  if (!words) return "Field";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function isOrganizationField(field) {
  return field === "organization" || String(field).endsWith("organization_id");
}

function organizationText(value, names) {
  if (value === null || value === undefined || value === "") return "empty";
  const label = names?.[String(value)];
  return label || String(value);
}

function displayValue(field, value, names) {
  if (isOrganizationField(field)) return organizationText(value, names);
  if (value === null || value === undefined || value === "") return "empty";
  if (String(field).startsWith("is_") && (value === 0 || value === 1 || value === true || value === false)) {
    return value === 1 || value === true ? "Yes" : "No";
  }
  return String(value);
}

function focusedChanges(changes, names) {
  const entries = Object.entries(changes || {});
  const orgEntries = entries.filter(([field]) => isOrganizationField(field));
  const otherEntries = entries.filter(([field]) => !isOrganizationField(field));
  const namedOrg = orgEntries.map(([, value]) => {
    if (!value || typeof value !== "object") return ["organization", value];
    return ["organization", {
      old: organizationText(value.old, names),
      new: organizationText(value.new, names),
    }];
  });

  let keptOrg = namedOrg;
  if (namedOrg.length > 1) {
    const first = namedOrg[0][1];
    const sameSwitch = namedOrg.every(([, value]) => value?.old === first?.old && value?.new === first?.new);
    keptOrg = sameSwitch ? [namedOrg[0]] : namedOrg;
  }

  return [...keptOrg, ...otherEntries];
}

const SCREEN_BY_MODEL = {
  InstituteSettings: { module: "Setup", feature: "Institute settings" },
  LeadActivities: { module: "Leads", feature: "Lead activities", form: "/lead-activities/edit" },
  Lead: { module: "Leads", feature: "Leads", form: "/leads/edit" },
  FollowupStructure: { module: "Leads", feature: "Follow-up structures", form: "/followup-structures/edit" },
  Employees: { module: "Employees", feature: "Employee details", form: "/organization/employee/employee-details/edit" },
  ApplicationOrganizationAcive: { module: "Organization", feature: "Active organization" },
};

function screenFromRow(row) {
  const known = SCREEN_BY_MODEL[row.model] || {};
  const feature = row.feature || known.feature || readableWords(row.model) || "Record";
  const moduleName = row.module || known.module || feature;
  const recordId = row.record_id || row.subject_id;
  const link =
    row.link ||
    (known.form && recordId && (row.action === "created" || row.action === "updated")
      ? `${known.form}/${recordId}`
      : null);
  return { module: moduleName, feature, link };
}

function changeValue(change) {
  if (change && typeof change === "object" && ("new" in change || "old" in change)) {
    const value = change.new ?? change.old;
    return value === null || value === undefined ? "" : String(value);
  }
  return change === null || change === undefined ? "" : String(change);
}

function leadIdFromRow(row) {
  const fromChange = changeValue(row?.changes?.organization_ems_lead_id).trim();
  if (fromChange) return fromChange;
  if (row?.model === "Lead" && row.record_id) return String(row.record_id);
  return "";
}

const FEATURE_LABEL = {
  InstituteSettings: "Institute settings",
  LeadActivities: "Lead activity",
  Lead: "Lead",
  FollowupStructure: "Follow-up structure",
  Employees: "Employee",
  ApplicationOrganizationAcive: "Organization",
};

function featureLabel(row) {
  return FEATURE_LABEL[row.model] || row.feature || SCREEN_BY_MODEL[row.model]?.feature || readableWords(row.model) || "Record";
}

function featureRecordId(row) {
  const changes = row.changes || {};
  if (row.model === "LeadActivities") {
    return String(changeValue(changes.organization_ems_lead_activity_id) || row.record_id || "").trim();
  }
  if (row.model === "Lead") {
    return String(row.record_id || changeValue(changes.organization_ems_lead_id) || "").trim();
  }
  if (row.model === "Employees") {
    return String(changeValue(changes.employee_id) || row.subject_id || row.record_id || "").trim();
  }
  if (row.model === "ApplicationOrganizationAcive") {
    return String(
      changeValue(changes.organization_id) ||
      changeValue(changes.user_current_active_organization_id) ||
      row.record_id ||
      ""
    ).trim();
  }
  return String(row.record_id || row.subject_id || "").trim();
}

function recordName(row, id, namesById, orgNames) {
  const changes = row.changes || {};
  const fromChanges = ["person_full_name", "full_name", "name", "email", "structure_name", "organization_short_name", "organization_name"]
    .map((key) => changeValue(changes[key]).trim())
    .find(Boolean);
  if (row.subject_name) return String(row.subject_name).trim();
  if (row.model === "Lead" || row.model === "LeadActivities") {
    const leadId = leadIdFromRow(row);
    return String(namesById?.[leadId] || namesById?.[id] || fromChanges || "").trim();
  }
  if (row.model === "ApplicationOrganizationAcive") {
    return String(orgNames?.[id] || fromChanges || "").trim();
  }
  return String(fromChanges || "").trim();
}

function identityText(row, namesById, orgNames) {
  const feature = featureLabel(row);
  const id = featureRecordId(row);
  const name = recordName(row, id, namesById, orgNames);
  const lines = [];
  if (id && name && row.model !== "LeadActivities") lines.push(`${feature} - ${id}, ${name}`);
  else if (id) lines.push(`${feature} - ${id}`);
  else if (name) lines.push(`${feature} - ${name}`);

  if (row.model === "LeadActivities") {
    const leadId = leadIdFromRow(row);
    const leadLine = leadId && name ? `${leadId}, ${name}` : leadId || name;
    if (leadLine && leadLine !== id) lines.push(leadLine);
  }
  return lines.join("\n") || "—";
}

function sameValue(left, right) {
  const normalize = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (value === true || value === false) return value ? "bool:1" : "bool:0";
    const text = String(value).trim();
    const time = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (time) {
      const hours = time[1].padStart(2, "0");
      const seconds = time[3] || "00";
      return `time:${hours}:${time[2]}:${seconds}`;
    }
    const word = text.toLowerCase();
    if (word === "true" || word === "yes") return "bool:1";
    if (word === "false" || word === "no") return "bool:0";
    return text;
  };

  return normalize(left) === normalize(right);
}

function detailLines(row, names) {
  const changes = focusedChanges(row.changes, names);
  return changes.map(([field, value]) => {
    const label = fieldLabel(field);
    if (!value || typeof value !== "object" || !("old" in value || "new" in value)) {
      return `${label}: ${value ?? "empty"}.`;
    }
    const oldText = displayValue(field, value.old, names);
    const newText = displayValue(field, value.new, names);
    if (row.action === "updated" && sameValue(value.old, value.new)) return null;
    if (row.action === "created") return `${label} was set to ${newText}.`;
    if (row.action === "deleted") return `${label} was ${oldText} before this record was deleted.`;
    if (oldText === "empty") return `${label} was set to ${newText}.`;
    if (newText === "empty") return `${label} was cleared. It was ${oldText}.`;
    return `${label} changed from ${oldText} to ${newText}.`;
  }).filter(Boolean);
}

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "deleted", label: "Deleted" },
  { value: "restored", label: "Restored" },
];

function performerLabel(row) {
  const name = row.performed_by_name || "System";
  return row.performed_by_email ? `${name} (${row.performed_by_email})` : name;
}

function boundTime(value, end) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  if (end && String(value).length === 16) date.setSeconds(59, 999);
  return date.getTime();
}

function rowMatchesFilters(row, filters) {
  const screen = screenFromRow(row);
  if (filters.by && performerLabel(row) !== filters.by) return false;
  if (filters.module && screen.module !== filters.module) return false;
  if (filters.feature && screen.feature !== filters.feature) return false;
  const time = new Date(row.created_at).getTime();
  const from = boundTime(filters.from, false);
  const to = boundTime(filters.to, true);
  if (from != null && (Number.isNaN(time) || time < from)) return false;
  if (to != null && (Number.isNaN(time) || time > to)) return false;
  return true;
}

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right));
}

function SelectFilter({ emptyLabel, value, onChange, options, minWidth = 180 }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((item) => item.value === value);
  const label = selected?.label || emptyLabel;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choices = [{ value: "", label: emptyLabel }, ...options];

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        style={{
          height: 42,
          minWidth,
          maxWidth: 280,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "0 14px",
          borderRadius: 12,
          border: open ? "1px solid var(--ems-primary, #0891b2)" : "1px solid #d7dbe7",
          background: "#fff",
          color: value ? "#1f2430" : "#6b7280",
          fontWeight: 650,
          fontSize: 13.5,
          fontFamily: "inherit",
          boxShadow: open ? "0 0 0 3px rgba(8, 145, 178, 0.16)" : "0 1px 2px rgba(16, 24, 40, 0.06)",
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ color: "#9095ab", fontSize: 12, transform: open ? "rotate(180deg)" : "none" }}>▼</span>
      </button>
      {open ? (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: 48,
            left: 0,
            zIndex: 20,
            minWidth: Math.max(minWidth, 190),
            maxHeight: 280,
            overflowY: "auto",
            padding: 6,
            borderRadius: 14,
            border: "1px solid #e6e8f0",
            background: "#fff",
            boxShadow: "0 16px 40px rgba(16, 24, 40, 0.14)",
          }}
        >
          {choices.map((item) => {
            const active = item.value === value;
            return (
              <button
                key={item.value || item.label}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(item.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: active ? "var(--ems-primary-tint, #e6f7fb)" : "transparent",
                  color: active ? "var(--ems-primary, #0891b2)" : "#1f2430",
                  fontWeight: active ? 700 : 550,
                  fontSize: 13.5,
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const dateFieldStyle = {
  height: 42,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "0 12px",
  borderRadius: 12,
  border: "1px solid #d7dbe7",
  background: "#fff",
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
};

const dateInputStyle = {
  height: 42,
  border: "none",
  outline: "none",
  background: "transparent",
  color: "#1f2430",
  fontWeight: 650,
  fontSize: 13.5,
  fontFamily: "inherit",
};

function ActivityLogList() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [action, setAction] = useState("");
  const [by, setBy] = useState("");
  const [moduleName, setModuleName] = useState("");
  const [feature, setFeature] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [orgNames, setOrgNames] = useState({});
  const [leadNames, setLeadNames] = useState({});

  useEffect(() => {
    const map = {};
    const add = (item) => {
      if (!item || item.organization_id == null) return;
      const label = item.organization_short_name || item.organization_name;
      if (label) map[String(item.organization_id)] = label;
    };
    const stored = [
      ...(Array.isArray(userData?.organizations) ? userData.organizations : []),
      ...(Array.isArray(userData?.organization) ? userData.organization : userData?.organization ? [userData.organization] : []),
    ];
    stored.forEach(add);
    setOrgNames({ ...map });

    const clientId = userData?.organization?.client_id || userData?.client_id;
    if (!clientId) return undefined;

    let cancelled = false;
    axios.get(`${MAIN_URL}/api/organizations/v1`, {
      headers: getApiHeaders(),
      params: { client_id: clientId, per_page: 200 },
    }).then((response) => {
      if (cancelled) return;
      const list = response?.data?.organizations?.data || [];
      list.forEach(add);
      setOrgNames({ ...map });
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [userData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!org?.organization_id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const list = [];
        let pageNo = 1;
        let last = 1;
        do {
          const response = await axios.get(`${MAIN_URL}/api/application/activity-logs`, {
            headers: getApiHeaders(),
            params: {
              organization_id: org.organization_id,
              page: pageNo,
              per_page: 100,
              search: appliedSearch,
              action,
            },
          });
          if (cancelled) return;
          const payload = response?.data?.activity_logs || {};
          const batch = Array.isArray(payload.data) ? payload.data : [];
          list.push(...batch);
          last = payload.last_page || 1;
          pageNo += 1;
        } while (pageNo <= last && pageNo <= 30);
        if (!cancelled) setRows(list);
      } catch (error) {
        if (cancelled) return;
        setRows([]);
        toast.error(error?.response?.data?.error || "Failed to load activity logs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id, appliedSearch, action]);

  const filteredRows = useMemo(
    () => rows.filter((row) => rowMatchesFilters(row, { by, module: moduleName, feature, from, to })),
    [rows, by, moduleName, feature, from, to]
  );
  const pageSize = 25;
  const lastPage = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, lastPage);
  const pageRows = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const byOptions = useMemo(
    () => uniqueSorted(rows.map((row) => performerLabel(row))).map((label) => ({ value: label, label })),
    [rows]
  );
  const moduleOptions = useMemo(
    () => uniqueSorted(rows.map((row) => screenFromRow(row).module)).map((label) => ({ value: label, label })),
    [rows]
  );
  const featureOptions = useMemo(
    () => uniqueSorted(
      rows
        .filter((row) => !moduleName || screenFromRow(row).module === moduleName)
        .map((row) => screenFromRow(row).feature)
    ).map((label) => ({ value: label, label })),
    [rows, moduleName]
  );
  const leadKey = pageRows.map((row) => `${row.application_activity_log_id}:${leadIdFromRow(row)}`).join("|");

  useEffect(() => {
    const ids = [...new Set(leadKey.split("|").map((item) => item.split(":")[1]).filter(Boolean))];
    if (ids.length === 0) {
      setLeadNames({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(ids.map(async (id) => {
      try {
        const leadResponse = await axios.get(`${MAIN_URL}/api/ems/leads/${id}`, { headers: getApiHeaders() });
        const lead = leadResponse?.data?.data;
        return [id, lead?.person_full_name || lead?.email || ""];
      } catch {
        return [id, ""];
      }
    })).then((pairs) => {
      if (!cancelled) setLeadNames(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [leadKey]);

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Activity Logs</h1>
          <div className="ems-page-sub">
            Creates, updates, and deletes for this organization.
          </div>
        </div>
      </div>

      <div className="ems-table-toolbar" style={{ display: "flex", gap: 12, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
        <form
          className="ems-search"
          style={{ flex: 1, maxWidth: 360 }}
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setAppliedSearch(search.trim());
          }}
        >
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search message, user, or field"
          />
        </form>
        <SelectFilter
          emptyLabel="All actions"
          value={action}
          options={ACTION_OPTIONS.filter((item) => item.value)}
          onChange={(next) => {
            setPage(1);
            setAction(next);
          }}
        />
        <SelectFilter
          emptyLabel="All users"
          value={by}
          options={byOptions}
          minWidth={200}
          onChange={(next) => {
            setPage(1);
            setBy(next);
          }}
        />
        <SelectFilter
          emptyLabel="All modules"
          value={moduleName}
          options={moduleOptions}
          onChange={(next) => {
            setPage(1);
            setModuleName(next);
            setFeature("");
          }}
        />
        <SelectFilter
          emptyLabel="All features"
          value={feature}
          options={featureOptions}
          onChange={(next) => {
            setPage(1);
            setFeature(next);
          }}
        />
        <label style={dateFieldStyle}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>From</span>
          <input
            type="datetime-local"
            aria-label="From"
            value={from}
            onChange={(event) => {
              setPage(1);
              setFrom(event.target.value);
            }}
            style={dateInputStyle}
          />
        </label>
        <label style={dateFieldStyle}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>To</span>
          <input
            type="datetime-local"
            aria-label="To"
            value={to}
            onChange={(event) => {
              setPage(1);
              setTo(event.target.value);
            }}
            style={dateInputStyle}
          />
        </label>
      </div>

      <div className="ems-card ems-table-card">
        <div className="ems-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Feature</TableCell>
                <TableCell>By</TableCell>
                <TableCell>What changed</TableCell>
                <TableCell>Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8}>Loading activity logs…</TableCell>
                </TableRow>
              )}
              {!loading && pageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    {rows.length === 0
                      ? "No activity yet. Create, update, or delete a record and it will appear here."
                      : "No activity matches these filters."}
                  </TableCell>
                </TableRow>
              )}
              {!loading && pageRows.map((row) => {
                const screen = screenFromRow(row);
                return (
                  <TableRow key={row.application_activity_log_id}>
                    <TableCell style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                      <div>{formatWhen(row.created_at).date}</div>
                      {formatWhen(row.created_at).time ? (
                        <div style={{ marginTop: 3, fontSize: 12.5, color: "#6b7280", fontWeight: 600 }}>
                          {formatWhen(row.created_at).time}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>{ACTION_LABELS[row.action] || row.action || "—"}</TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>{screen.module || "—"}</TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>{screen.feature || "—"}</TableCell>
                    <TableCell style={{ verticalAlign: "top" }}>
                      {performerLabel(row)}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", minWidth: 180, whiteSpace: "pre-line" }}>
                      {identityText(row, leadNames, orgNames)}
                    </TableCell>
                    <TableCell style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {screen.link && (row.action === "created" || row.action === "updated") ? (
                        <Link to={screen.link} style={{ color: "var(--ems-primary, #0891b2)", fontWeight: 650 }}>
                          Open
                        </Link>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <span className="ems-page-sub">{filteredRows.length} entries</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="ems-btn ems-sm"
            disabled={safePage <= 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span className="ems-page-sub">Page {safePage} of {lastPage}</span>
          <button
            type="button"
            className="ems-btn ems-sm"
            disabled={safePage >= lastPage || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActivityLogList;
